/** The address fields selected by LOAD_AGENCY_RESOURCES. */
export type ResourceAddressItem = {
  formattedAddress?: string | null;
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  coordinates?: {
    latitude?: number | null;
    longitude?: number | null;
  } | null;
};

/** The phone fields selected by LOAD_AGENCY_RESOURCES. */
export type ResourcePhoneItem = {
  number?: string | null;
  description?: string | null;
  actions?: Array<string | null> | null;
};

/** The location fields selected by LOAD_AGENCY_RESOURCES. */
export type ResourceLocationItem = {
  name?: string | null;
  website?: string | null;
  address?: ResourceAddressItem | null;
  phones?: Array<ResourcePhoneItem | null> | null;
};

export type PhoneView = {
  /** As entered, for display. */
  number: string;
  /** `tel:` link, or undefined when the number cannot be called. */
  href?: string;
  extension?: string;
  description?: string;
};

export type WebsiteLink = {
  href: string;
  /** The host, shown as the link text. */
  label: string;
};

export type ResourceLocationView = {
  name?: string;
  addressLines: string[];
  phones: PhoneView[];
  website?: WebsiteLink;
  directionsHref?: string;
};

const clean = (value: string | null | undefined): string =>
  (value ?? "").trim();

/**
 * A link for the website, or undefined. A value without a scheme gets
 * `http://`, as on iOS. Only http(s) URLs with a host survive, so
 * `javascript:` or `data:` text can never become a link.
 */
export const websiteLink = (
  raw: string | null | undefined,
): WebsiteLink | undefined => {
  const value = clean(raw);
  if (!value) return undefined;
  // Some other scheme ("ftp://", "mailto:", "javascript:") is not a website;
  // prefixing it would make a nonsense link. "host:8080" is still a host.
  const scheme = value.match(/^([a-z][a-z0-9+.-]*):(?!\d)/i)?.[1];
  if (scheme && !/^https?$/i.test(scheme)) return undefined;
  const withScheme = /^https?:\/\//i.test(value) ? value : `http://${value}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return undefined;
  }
  // Always http(s) with a host here: the scheme is checked or added above,
  // and the URL parser rejects an http URL without a host.
  return { href: url.href, label: url.host };
};

// "816-555-0101 x12", "ext. 12", "extension 12": the extension is shown as
// text and dialled after a pause (a comma in the tel: link).
const EXTENSION = /\s*(?:,|x|ext\.?|extension)\s*(\d+)\s*$/i;

/**
 * Whether the number can be called. The backend sends the PhoneAction enum
 * names ("Call", "Text"); a number with no actions is treated as callable.
 */
export const isCallable = (
  actions: Array<string | null> | null | undefined,
): boolean => {
  const values = (actions ?? []).filter((action): action is string =>
    Boolean(action),
  );
  return (
    values.length === 0 ||
    values.some((action) => action.trim().toLowerCase() === "call")
  );
};

const KEYPAD = ["ABC", "DEF", "GHI", "JKL", "MNO", "PQRS", "TUV", "WXYZ"];
const keypadDigit = (letter: string): string =>
  String(KEYPAD.findIndex((keys) => keys.includes(letter)) + 2);

export const phoneView = (phone: ResourcePhoneItem): PhoneView | undefined => {
  const raw = clean(phone.number);
  if (!raw) return undefined;
  const extensionMatch = raw.match(EXTENSION);
  const main = extensionMatch ? raw.slice(0, extensionMatch.index).trim() : raw;
  // Vanity numbers ("1-800-FLOWERS") dial the letters' keypad digits; text
  // with no digits at all ("Ask at the desk") is not a number.
  const digits = /\d/.test(main)
    ? main.toUpperCase().replace(/[A-Z]/g, keypadDigit).replace(/\D/g, "")
    : "";
  const plus = main.startsWith("+") ? "+" : "";
  const description = clean(phone.description);
  return {
    number: main,
    href:
      digits && isCallable(phone.actions)
        ? `tel:${plus}${digits}${extensionMatch ? `,${extensionMatch[1]}` : ""}`
        : undefined,
    extension: extensionMatch?.[1],
    description: description || undefined,
  };
};

/** Formatted address when there is one, else street then "city, state postal". */
export const addressLines = (
  address: ResourceAddressItem | null | undefined,
): string[] => {
  if (!address) return [];
  const formatted = clean(address.formattedAddress);
  if (formatted) return [formatted];
  const cityLine = [
    [clean(address.city), clean(address.state)].filter(Boolean).join(", "),
    clean(address.postalCode),
  ]
    .filter(Boolean)
    .join(" ");
  return [clean(address.streetAddress), cityLine].filter(Boolean);
};

const MAPS_DIRECTIONS = "https://www.google.com/maps/dir/?api=1&destination=";

/**
 * A maps link to the location: coordinates when they are real (finite, not
 * the 0,0 placeholder), else the address text. Google's universal URL opens
 * the maps app on phones and the website elsewhere.
 */
export const directionsUrl = (
  address: ResourceAddressItem | null | undefined,
): string | undefined => {
  const latitude = address?.coordinates?.latitude;
  const longitude = address?.coordinates?.longitude;
  if (
    typeof latitude === "number" &&
    typeof longitude === "number" &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    !(latitude === 0 && longitude === 0)
  ) {
    return `${MAPS_DIRECTIONS}${latitude},${longitude}`;
  }
  const text = addressLines(address).join(", ");
  return text ? `${MAPS_DIRECTIONS}${encodeURIComponent(text)}` : undefined;
};

export const toLocationView = (
  location: ResourceLocationItem,
): ResourceLocationView => {
  const name = clean(location.name);
  return {
    name: name || undefined,
    addressLines: addressLines(location.address),
    phones: (location.phones ?? [])
      .map((phone) => (phone ? phoneView(phone) : undefined))
      .filter((phone): phone is PhoneView => Boolean(phone)),
    website: websiteLink(location.website),
    directionsHref: directionsUrl(location.address),
  };
};

const isEmpty = (location: ResourceLocationView): boolean =>
  !location.name &&
  location.addressLines.length === 0 &&
  location.phones.length === 0 &&
  !location.website &&
  !location.directionsHref;

// A list entry is dropped when it is the main location again and adds
// nothing to it: same name and address, no phone or website the main lacks.
const addsNothingTo = (
  copy: ResourceLocationView,
  main: ResourceLocationView,
): boolean =>
  copy.name === main.name &&
  copy.addressLines.join("\n") === main.addressLines.join("\n") &&
  copy.phones.every((phone) =>
    main.phones.some((other) => other.number === phone.number),
  ) &&
  (!copy.website || copy.website.href === main.website?.href);

/**
 * The main location first, then the others, as on iOS. The main location is
 * not repeated if it is also in the list, and locations with nothing to show
 * are dropped.
 */
export const toLocationViews = (
  mainLocation: ResourceLocationItem | null | undefined,
  locations: Array<ResourceLocationItem | null> | null | undefined,
): { main?: ResourceLocationView; others: ResourceLocationView[] } => {
  const main = mainLocation ? toLocationView(mainLocation) : undefined;
  const others = (locations ?? [])
    .filter((location): location is ResourceLocationItem => Boolean(location))
    .map(toLocationView)
    .filter((location) => !main || !addsNothingTo(location, main))
    .filter((location) => !isEmpty(location));
  return { main: main && !isEmpty(main) ? main : undefined, others };
};
